import { useState } from 'react'
import clsx from 'clsx'
import {
  AlertTriangle, Bug, CheckCircle2, ChevronDown, ChevronRight, ClipboardCheck, Eye, EyeOff, HelpCircle,
  KeyRound, Loader2, LogIn, LogOut, PauseCircle, PlayCircle, ShieldAlert, ShieldOff, XCircle, Accessibility,
} from 'lucide-react'
import { useMdmEvents } from '../../../hooks/useMdm'
import { formatDateTime, formatDateTimeSeconds } from '../../../utils/format'
import { errorMessage, formatMinutes, formatUntil } from './mobileUtils'

const TONE = {
  info: 'bg-gray-100 text-gray-600',
  warn: 'bg-amber-100 text-amber-700',
  danger: 'bg-red-100 text-red-700',
  ok: 'bg-green-100 text-green-700',
}

const RESUME_REASON = {
  timeout: 'acabou o prazo',
  idle: 'o tablet ficou sem uso',
  reboot: 'o tablet reiniciou',
  user: 'o técnico tocou em "Voltar agora"',
  command: 'comando do painel',
}

const PURPOSE_LABEL = { admin: 'modo técnico', remove: 'remover o MDM' }

const withReason = (text, reason) => (reason ? `${text} — ${RESUME_REASON[reason] || reason}` : text)

const forPeriod = (d) => {
  const parts = []
  if (d?.minutes != null) parts.push(`por ${formatMinutes(d.minutes)}`)
  if (d?.until) parts.push(`(até ${formatUntil(d.until)})`)
  return parts.length ? ` ${parts.join(' ')}` : ''
}

/** Texto em português de cada evento (CONTRACT.md §12.5 + `code.issued` do painel). */
function describe(ev) {
  const d = ev.data || {}
  switch (ev.type) {
    case 'admin.enter': {
      const via = d.via === 'pin' ? ' com o PIN do grupo' : d.via === 'code' ? ' com código do painel' : ''
      const taps = d.taps ?? d.gestureTaps
      const trigger = d.trigger === 'taps'
        ? (taps ? `${taps} toques` : 'toques no logotipo')
        : d.trigger === 'volume' ? 'botões de volume' : null
      return { icon: KeyRound, tone: 'warn', title: `Acesso do técnico${via}${trigger ? ` (${trigger})` : ''}` }
    }
    case 'admin.fail':
      return {
        icon: ShieldAlert,
        tone: 'warn',
        title: 'Código ou PIN errado no acesso do técnico',
        detail: d.attempts != null ? `${d.attempts} tentativa(s) errada(s) seguida(s)` : null,
      }
    case 'admin.lockout':
      return {
        icon: ShieldAlert,
        tone: 'danger',
        title: `Acesso do técnico bloqueado${d.minutes != null ? ` por ${formatMinutes(d.minutes)}` : ''}`,
        detail: 'Muitos códigos errados seguidos.',
      }
    case 'kiosk.suspend':
      return { icon: LogOut, tone: 'warn', title: `Saiu do quiosque${forPeriod(d)}` }
    case 'kiosk.resume':
      return { icon: LogIn, tone: 'ok', title: withReason('Voltou ao quiosque', d.reason) }
    case 'mdm.pause':
      return { icon: PauseCircle, tone: 'warn', title: `MDM pausado${forPeriod(d)}`, detail: 'Quiosque e restrições de uso suspensos; VPN, localização e comandos continuam.' }
    case 'mdm.resume':
      return { icon: PlayCircle, tone: 'ok', title: withReason('MDM retomado', d.reason) }
    case 'mdm.remove':
      return {
        icon: ShieldOff,
        tone: 'danger',
        title: 'MDM removido no tablet',
        detail: 'O tablet saiu do gerenciamento. Para voltar, cadastre de novo pelo cabo USB.',
      }
    case 'kiosk.crash':
      return { icon: Bug, tone: 'warn', title: 'App do quiosque fechou sozinho', mono: d.packageName }
    case 'kiosk.crashloop':
      return {
        icon: AlertTriangle,
        tone: 'danger',
        title: `App do quiosque travando${d.count != null ? `: fechou ${d.count} vezes seguidas` : ''}`,
        mono: d.packageName,
      }
    case 'screen.consent':
      if (d.result === 'granted') return { icon: Eye, tone: 'info', title: 'A pessoa no tablet permitiu ver a tela' }
      if (d.result === 'denied') return { icon: EyeOff, tone: 'warn', title: 'A pessoa no tablet recusou o acesso à tela' }
      if (d.result === 'timeout') return { icon: EyeOff, tone: 'info', title: 'Pedido de acesso à tela ficou sem resposta no tablet' }
      return { icon: Eye, tone: 'info', title: 'Pedido de acesso à tela', mono: d.result }
    case 'screen.ended':
      return { icon: EyeOff, tone: 'info', title: 'A pessoa no tablet encerrou o compartilhamento de tela' }
    case 'accessibility.off':
      return { icon: Accessibility, tone: 'warn', title: 'Acessibilidade do agente desligada no tablet' }
    case 'accessibility.on':
      return {
        icon: Accessibility, tone: 'ok',
        title: d.via === 'secureSettings' ? 'Acessibilidade religada sozinha pelo agente' : 'Acessibilidade ligada por quem está no tablet',
      }
    case 'diagnostics.report': {
      const checks = Array.isArray(d.checks) ? d.checks : []
      const failed = checks.filter((c) => !c.ok).length
      return {
        icon: ClipboardCheck,
        tone: failed ? 'warn' : 'ok',
        title: 'Diagnóstico enviado pelo tablet',
        detail: checks.length
          ? (failed ? `${failed} de ${checks.length} itens com problema` : `Tudo ok (${checks.length} itens)`)
          : 'Sem itens.',
        checks,
      }
    }
    case 'code.issued':
      return {
        icon: KeyRound,
        tone: d.purpose === 'remove' ? 'danger' : 'info',
        title: `Código de acesso gerado no painel — ${PURPOSE_LABEL[d.purpose] || d.purpose || 'propósito desconhecido'}`,
        detail: d.reason ? `Motivo: ${d.reason}` : null,
      }
    default:
      return { icon: HelpCircle, tone: 'info', title: ev.type, unknown: true }
  }
}

/** Histórico de eventos locais do tablet (acesso do técnico, quiosque, tela remota, diagnóstico). */
export default function EventsTab({ deviceId }) {
  const [limit, setLimit] = useState(100)
  const { data: events = [], isLoading, error, isFetching } = useMdmEvents(deviceId, limit)
  const [open, setOpen] = useState(() => new Set())

  const toggle = (id) => setOpen((prev) => {
    const next = new Set(prev)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    return next
  })

  if (isLoading) {
    return <div className="py-8 text-center text-gray-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Carregando histórico...</div>
  }

  return (
    <div className="space-y-2">
      <p className="text-sm text-gray-500">
        O que aconteceu no próprio tablet (acesso do técnico, quiosque, tela remota, diagnóstico) e os códigos gerados no painel.
        Eventos de quando o tablet estava sem internet chegam assim que ele volta.
      </p>
      {error && <p className="text-sm text-red-600">{errorMessage(error, 'Erro ao carregar o histórico.')}</p>}

      <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100">
        {events.length === 0 ? (
          <p className="px-3 py-6 text-center text-sm text-gray-500">
            Nada registrado ainda. O histórico aparece a partir do agente 1.1.
          </p>
        ) : events.map((ev) => {
          const info = describe(ev)
          const Icon = info.icon
          const expandable = (info.checks && info.checks.length > 0) || info.unknown
          const isOpen = open.has(ev.id)
          const late = ev.receivedAt && ev.occurredAt
            && new Date(ev.receivedAt).getTime() - new Date(ev.occurredAt).getTime() > 5 * 60_000
          return (
            <div key={ev.id} className="px-3 py-2.5">
              <div className="flex items-start gap-3">
                <span className={clsx('mt-0.5 p-1.5 rounded-full flex-shrink-0', TONE[info.tone])}>
                  <Icon className="w-4 h-4" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className={clsx('text-sm text-gray-900', info.unknown && 'font-mono')}>{info.title}</div>
                  {info.detail && <div className="text-xs text-gray-600">{info.detail}</div>}
                  {info.mono && <div className="text-xs text-gray-500 font-mono break-all">{info.mono}</div>}
                  {expandable && (
                    <button type="button" onClick={() => toggle(ev.id)}
                      className="mt-1 inline-flex items-center gap-0.5 text-xs text-theme-primary hover:underline">
                      {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                      {info.checks ? 'Ver itens do diagnóstico' : 'Ver dados'}
                    </button>
                  )}
                  {isOpen && info.checks && <DiagnosticsList checks={info.checks} />}
                  {isOpen && info.unknown && (
                    <pre className="mt-1 p-2 bg-gray-50 rounded text-xs text-gray-700 whitespace-pre-wrap break-all">
                      {JSON.stringify(ev.data ?? null, null, 2)}
                    </pre>
                  )}
                </div>
                <div className="text-right text-xs text-gray-500 whitespace-nowrap flex-shrink-0">
                  <div title={ev.receivedAt ? `Recebido em ${formatDateTimeSeconds(ev.receivedAt)}` : undefined}>
                    {formatDateTimeSeconds(ev.occurredAt)}
                  </div>
                  <div>{ev.source === 'panel' ? `Painel${ev.userName ? ` · ${ev.userName}` : ''}` : 'Tablet'}</div>
                  {late && <div className="text-amber-700">chegou em {formatDateTime(ev.receivedAt)}</div>}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {events.length >= limit && limit < 500 && (
        <div className="text-center">
          <button type="button" onClick={() => setLimit(500)} disabled={isFetching}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
            {isFetching && <Loader2 className="w-4 h-4 animate-spin" />}
            Mostrar mais
          </button>
        </div>
      )}
    </div>
  )
}

function DiagnosticsList({ checks }) {
  return (
    <ul className="mt-2 space-y-1 border-l-2 border-gray-100 pl-3">
      {checks.map((c, i) => (
        <li key={c.id || i} className="flex items-start gap-2 text-sm">
          {c.ok
            ? <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
            : <XCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />}
          <span>
            <span className="text-gray-900">{c.label || c.id}</span>
            {c.detail && <span className="block text-xs text-gray-500">{c.detail}</span>}
          </span>
        </li>
      ))}
    </ul>
  )
}
